// Build an Error carrying the HTTP status so callers can distinguish
// auth failures from server/network errors when rendering error states.
// 401 gets fixed user-facing copy; otherwise prefer the server's message.
async function responseError(response, fallbackMessage) {
  let message = fallbackMessage;
  if (response.status === 401) {
    message = 'Your session has expired. Please sign in again.';
  } else {
    try {
      const body = await response.json();
      message = body.message || body.error || fallbackMessage;
    } catch {
      // Non-JSON error body — keep the fallback message
    }
  }
  const error = new Error(message);
  error.status = response.status;
  return error;
}

class TimezoneGroupsService {
  constructor() {
    this.baseUrl = '/api/timezone-groups';
    this.getAuthToken = null;
  }

  setAuthTokenGetter(getAuthToken) {
    this.getAuthToken = getAuthToken;
  }

  async getAllGroups(authToken = null) {
    // Use provided token or get from auth context
    const token = authToken || (this.getAuthToken ? await this.getAuthToken() : null);
    
    // No auth token = genuinely signed out; an empty list is the truth here.
    // Any failure past this point throws so callers can show an error state
    // instead of rendering a 401/500 as "user has no groups".
    if (!token) {
      return [];
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(this.baseUrl, { headers });

    if (!response.ok) {
      throw await responseError(response, 'Failed to load timezone groups');
    }

    const data = await response.json();
    const userGroups = data.data || [];

    // Transform database format to match frontend expectations
    return userGroups.map(group => ({
      id: group.id,
      name: group.name,
      cities: group.cities,
      isDefault: group.name === 'Default', // Mark the "Default" group as default
      createdAt: group.created_at,
      updatedAt: group.updated_at
    }));
  }

  async createGroup(name, cities, authToken = null) {
    // Use provided token or get from auth context
    const token = authToken || (this.getAuthToken ? await this.getAuthToken() : null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name, cities }),
      });

      if (!response.ok) {
        const error = await response.json();
        if (error.limitType === 'zone_limit') {
          throw new Error(`Zone limit exceeded: ${error.message}`);
        }
        throw new Error(error.message || 'Failed to create timezone group');
      }

      const data = await response.json();
      return {
        id: data.data.id,
        name: data.data.name,
        cities: data.data.cities,
        isDefault: false,
        createdAt: data.data.created_at,
        updatedAt: data.data.updated_at
      };
    } catch (error) {
      console.error('Error creating timezone group:', error);
      throw error;
    }
  }

  async updateGroup(groupId, updates, authToken = null) {
    // Use provided token or get from auth context
    const token = authToken || (this.getAuthToken ? await this.getAuthToken() : null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}?groupId=${groupId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update timezone group');
      }

      const data = await response.json();
      return {
        id: data.data.id,
        name: data.data.name,
        cities: data.data.cities,
        isDefault: false,
        createdAt: data.data.created_at,
        updatedAt: data.data.updated_at
      };
    } catch (error) {
      console.error('Error updating timezone group:', error);
      throw error;
    }
  }

  async deleteGroup(groupId, authToken = null) {
    // Use provided token or get from auth context
    const token = authToken || (this.getAuthToken ? await this.getAuthToken() : null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}?groupId=${groupId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete timezone group');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error deleting timezone group:', error);
      throw error;
    }
  }

  async setActiveGroup(groupId, authToken = null) {
    // Use provided token or get from auth context
    const token = authToken || (this.getAuthToken ? await this.getAuthToken() : null);
    
    // If no auth token available, throw error
    if (!token) {
      throw new Error('No auth token available for setting active group');
    }
    
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'setActive', groupId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to set active timezone group');
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error setting active timezone group (likely API server not running):', error.message);
      throw error;
    }
  }

  async getActiveGroup(authToken = null) {
    // Use provided token or get from auth context
    const token = authToken || (this.getAuthToken ? await this.getAuthToken() : null);
    
    // No auth token = genuinely signed out; null is the truth here.
    // Any failure past this point throws so callers can distinguish
    // "no active group" from a failed request.
    if (!token) {
      return null;
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(`${this.baseUrl}?action=active`, { headers });

    if (!response.ok) {
      throw await responseError(response, 'Failed to get active timezone group');
    }

    const data = await response.json();
    if (!data.data) {
      return null;
    }

    return {
      id: data.data.id,
      name: data.data.name,
      cities: data.data.cities,
      isDefault: data.data.name === 'Default',
      createdAt: data.data.created_at,
      updatedAt: data.data.updated_at
    };
  }

  async loadGroup(groupId, authToken = null) {
    try {
      // First set as active group, then get all groups to find the one we want
      await this.setActiveGroup(groupId, authToken);
      const allGroups = await this.getAllGroups(authToken);
      const group = allGroups.find(g => g.id === groupId);
      
      if (!group) {
        throw new Error('Timezone group not found');
      }
      
      return group;
    } catch (error) {
      console.error('Error loading timezone group:', error);
      throw error;
    }
  }

  hasChangesFromActiveGroup(currentTimezones, activeGroup) {
    if (!activeGroup) return currentTimezones.length > 0;
    
    // Compare current timezones with active group
    if (currentTimezones.length !== activeGroup.cities.length) return true;
    
    // Check both content and order
    return currentTimezones.some((tz, index) => {
      const groupCity = activeGroup.cities[index];
      return !groupCity || tz.name !== groupCity.name;
    });
  }

  async updateActiveGroupWithCurrentTimezones(currentTimezones, activeGroup, authToken = null) {
    if (!activeGroup) {
      throw new Error('No active group to update');
    }

    return await this.updateGroup(activeGroup.id, {
      cities: currentTimezones.map(tz => ({
        name: tz.name,
        timezone: tz.timezone
      }))
    });
  }
}

export const timezoneGroupsService = new TimezoneGroupsService();
export default timezoneGroupsService;